// src/pages/HiringCircuit.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  LightningIcon,
  StackIcon,
  HourglassMediumIcon,
  GhostIcon,
  ChartLineDownIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
} from "@phosphor-icons/react";
import "../styles/hiring-circuit.css";
import "../styles/ai-talent-drive.css";
import DriveHero from "../components/aiTalentDrive/DriveHero";
import { API_BASE as DRIVE_API_BASE } from "../components/aiTalentDrive/api";
import { PainLines, VelocityMarquee } from "../components/aiTalentDrive/driveMotion";
import JobDescriptionModal from "../components/aiTalentDrive/JobDescriptionModal";
import DriveCompanyLogos from "../components/aiTalentDrive/DriveCompanyLogos";
import DriveProof from "../components/aiTalentDrive/DriveProof";
import CandidateRatings from "../components/aiTalentDrive/CandidateRatings";
import DriveReels from "../components/aiTalentDrive/DriveReels";
import useRevealOnScroll from "../components/aiTalentDrive/useRevealOnScroll";
import {
  ANY_EXPERIENCE,
  EXPERIENCE_CODES,
  STUDENT,
  TRACKS,
  TRACK_GROUPS,
  bandsFor,
  splitTrack,
} from "../components/aiTalentDrive/tracks";



// Registration + payment API (babblebots-monolith: companyapp/hiring_circuit_views.py)
// `npm start` talks to a locally running backend; every deployed build talks
// to production unless REACT_APP_HIRING_CIRCUIT_API says otherwise.
const API_BASE = DRIVE_API_BASE;
const CHECKOUT_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

// "Where did you hear about us?" — codes the registration API expects
// (companyapp/models.py HiringCircuitRegistration.HEARD_FROM_CHOICES).
const HEARD_FROM_OPTIONS: { code: string; label: string }[] = [
  { code: "mtw", label: "MTW" },
  { code: "instagram", label: "Instagram" },
  { code: "ai_assistant", label: "ChatGPT/Gemini/Agents" },
  { code: "linkedin", label: "LinkedIn" },
  { code: "naukri", label: "Naukri" },
  { code: "community", label: "WhatsApp/Discord community" },
  { code: "referral", label: "Referral from a friend" },
  { code: "others", label: "Others" },
];


// Codes the API expects for each dropdown label.
const trackCode = (label: string) => label.split(" ")[0].replace("–", "-");
const experienceCode = (label: string) => EXPERIENCE_CODES[label] || label;

// The order being paid for is remembered here so the page can pick the
// registration back up after a trip to a UPI app, a reload, or a redirect.
const PENDING_ORDER_KEY = "hc-pending-order";
const PENDING_ORDER_MAX_AGE_MS = 2 * 60 * 60 * 1000;

const readPendingOrder = (): string | null => {
  try {
    const raw = window.localStorage.getItem(PENDING_ORDER_KEY);
    if (!raw) return null;
    const { orderId, at } = JSON.parse(raw);
    if (!orderId || Date.now() - at > PENDING_ORDER_MAX_AGE_MS) {
      window.localStorage.removeItem(PENDING_ORDER_KEY);
      return null;
    }
    return orderId;
  } catch {
    return null;
  }
};
const writePendingOrder = (orderId: string | null) => {
  try {
    if (orderId)
      window.localStorage.setItem(
        PENDING_ORDER_KEY,
        JSON.stringify({ orderId, at: Date.now() })
      );
    else window.localStorage.removeItem(PENDING_ORDER_KEY);
  } catch {
    /* storage blocked (private mode): the ?order= redirect still works */
  }
};

let checkoutScriptPromise: Promise<void> | null = null;
const loadCheckoutScript = () => {
  if ((window as any).Razorpay) return Promise.resolve();
  if (!checkoutScriptPromise) {
    checkoutScriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = CHECKOUT_SCRIPT;
      script.onload = () => resolve();
      script.onerror = () => {
        checkoutScriptPromise = null;
        reject(new Error("checkout script failed to load"));
      };
      document.body.appendChild(script);
    });
  }
  return checkoutScriptPromise;
};

// The packages, in the order they are shown. Amounts are only for display:
// the backend decides what is charged from the package id (PACKAGES in
// companyapp/hiring_circuit.py). `minTracks`..`maxTracks` is how many tracks
// the candidate picks: one for Basic and Premium; Max needs at least three
// and may take every track there is, for the same ₹799. The ids are what the
// API exchanges and never change; the names did (2026-10-25: Get Discovered
// → Basic, Get Interviewed → Premium, Maximize Your Chances → Max).
type PackageId = "discover" | "interview" | "maximize";
type Package = {
  id: PackageId;
  name: string;
  price: number;
  // Set only on a package whose price was cut: the old price is shown
  // struck through next to the new one.
  originalPrice?: number;
  minTracks: number;
  maxTracks: number;
  tagline: string;
  // A highlighted second line in the tagline slot (which has room for two lines)
  boost?: string;
  points: string[];
  recommended?: boolean;
};
const PACKAGES: Package[] = [
  {
    id: "discover",
    name: "Basic",
    price: 0,
    originalPrice: 99,
    minTracks: 1,
    maxTracks: 1,
    tagline: "Your CV in front of the hiring companies",
    points: ["1 track", "Profile and CV visible to companies", "CV–role match score", "No AI-Interview"],
  },
  {
    id: "interview",
    name: "Premium",
    price: 199,
    originalPrice: 399,
    minTracks: 1,
    maxTracks: 1,
    tagline: "The AI-Interview, routed to every company",
    boost: "Recruiters watch your video directly",
    points: ["1 track", "AI-Interview with Tina", "Personalised feedback", "Profile, CV and match score"],
    recommended: true,
  },
  {
    id: "maximize",
    name: "Max",
    price: 799,
    originalPrice: 999,
    minTracks: 3,
    maxTracks: TRACKS.length,
    tagline: "Three or more tracks, one payment",
    points: [`3 to ${TRACKS.length} tracks of your choice`, "An AI-Interview per track", "Feedback on each", "One payment, whatever the count"],
  },
];
const packageById = (id: string): Package =>
  PACKAGES.find((p) => p.id === id) || PACKAGES[1];
const formatRupees = (amount: number) => `\u20B9${amount.toLocaleString("en-IN")}`;

// A package's price, with its old price struck through when it has one
// ("discover", cut from \u20B999 to free; "interview" \u20B9399 to \u20B9199 (via \u20B9299, 2026-10-29 to 2026-11-04) and
// "maximize" \u20B9999 to \u20B9799 on 2026-10-29).
const PriceDisplay = ({ pkg }: { pkg: Package }) => (
  <>
    {formatRupees(pkg.price)}
    {pkg.originalPrice ? (
      <s className="pk-price-strike">{formatRupees(pkg.originalPrice)}</s>
    ) : null}
  </>
);

// The comparison rows, in the order the table shows them. A cell is a
// tick, a dash, a short label, or null to leave the row off that card.
type Cell = true | false | string | null;
const PACKAGE_ROWS: { label: string; cells: Record<PackageId, Cell> }[] = [
  { label: "Tracks", cells: { discover: "1", interview: "1", maximize: `3 to ${TRACKS.length}` } },
  { label: "Profile and video visible to companies", cells: { discover: null, interview: true, maximize: true } },
  { label: "Live dashboard", cells: { discover: true, interview: true, maximize: true } },
  { label: "CV upload", cells: { discover: true, interview: true, maximize: `\u2713 \u00D7 3 to ${TRACKS.length} tracks` } },
  { label: "AI-Interview", cells: { discover: null, interview: true, maximize: `\u2713 \u00D7 3 to ${TRACKS.length} tracks` } },
  { label: "Personalised feedback", cells: { discover: null, interview: true, maximize: true } },
  { label: "Price / track", cells: { discover: null, interview: "\u20B9199", maximize: `from \u20B9${Math.ceil(799 / TRACKS.length)}` } },
];

// The three packages as tickets, one card each, with a radio button apiece;
// the chosen one is lit. The label's ::after covers the card, so the whole
// card is the click target, and the radios give arrow-key selection for free.
// A package in `full` (the API's config.full: its seats are taken) keeps its
// card, greyed out and marked "Seats full", but cannot be picked.
function PackageCards({
  selected,
  full,
  onSelect,
}: {
  selected: PackageId;
  full: ReadonlySet<PackageId>;
  onSelect: (id: PackageId) => void;
}) {
  return (
    <div className="td-pk" role="radiogroup" aria-label="Choose your package">
      {PACKAGES.map((option, idx) => {
        const on = option.id === selected;
        const isFull = full.has(option.id);
        const inputId = `td-pk-${option.id}`;
        return (
          <div className="td-pk-card" data-selected={on} data-full={isFull} key={option.id}>
            <div className="td-pk-top">
              <span className="td-pk-idx">{String(idx + 1).padStart(2, "0")}</span>
              {isFull ? (
                <span className="td-pk-full">Seats full</span>
              ) : (
                option.recommended && <span className="td-pk-rec">Recommended</span>
              )}
            </div>
            <h3 className="td-pk-name">
              <label className="td-pk-pick" htmlFor={inputId}>
                {option.name}
              </label>
            </h3>
            <p className="td-pk-price">
              <PriceDisplay pkg={option} />
            </p>
            <p className="td-pk-tag" id={`${inputId}-tag`}>
              {option.tagline}
              {option.boost && <b className="td-pk-boost">{option.boost}</b>}
            </p>
            <ul className="td-pk-rows">
              {PACKAGE_ROWS.filter((row) => row.cells[option.id] !== null).map((row) => {
                const cell = row.cells[option.id];
                const off = cell === false;
                const text = typeof cell === "string" ? cell.replace(/^\u2713\s*/, "") : "";
                return (
                  <li data-off={off} key={row.label}>
                    <span className="td-pk-mark" aria-hidden="true">
                      {off ? (
                        <svg viewBox="0 0 12 12" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                          <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" />
                        </svg>
                      ) : (
                        "\u2713"
                      )}
                    </span>
                    <span>
                      {row.label}
                      {off && <span className="td-sr"> (not included)</span>}
                      {text &&
                        (typeof cell === "string" && cell.startsWith("\u2713") ? (
                          <b className="td-pk-x">{` ${text}`}</b>
                        ) : (
                          <b>{`: ${text}`}</b>
                        ))}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="td-pk-foot">
              <input
                type="radio"
                id={inputId}
                name="td-package"
                className="td-pk-input"
                value={option.id}
                checked={on}
                disabled={isFull}
                aria-describedby={`${inputId}-tag`}
                onChange={() => onSelect(option.id)}
              />
              <span className="td-pk-state" aria-hidden="true">
                {isFull ? "Seats are full" : on ? "Selected" : "Select"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

type Pick = { track: string };
const emptyPicks = (count: number): Pick[] =>
  Array.from({ length: count }, () => ({ track: "" }));

// One "years of experience" pick serves every chosen track: a track that is
// offered at that band takes it, a track with a single fixed band (open to
// all / student) takes its own. "" = still to pick.
const experienceFor = (track: string, yoe: string) => {
  const bands = bandsFor(track);
  if (bands.length === 1) return bands[0];
  return bands.includes(yoe) ? yoe : "";
};
// The bands the shared dropdown offers: the experience bands until a track
// is chosen, then the bands of the chosen tracks that come in more than one
// (a track with a single fixed band fills itself and is not asked about).
const EXPERIENCE_BANDS = ["0–2 years", "2–5 years"];
const sharedBands = (tracks: string[]) => {
  if (tracks.length === 0) return EXPERIENCE_BANDS;
  const seen = new Set<string>();
  tracks.forEach((track) => bandsFor(track).forEach((band) => bandsFor(track).length > 1 && seen.add(band)));
  return [...EXPERIENCE_BANDS, ANY_EXPERIENCE, STUDENT].filter((band) => seen.has(band));
};

type Confirmation = {
  trackName: string;
  trackNames: string[];
  packageId: PackageId;
  packageLabel: string;
  amount: number;
  // Empty for a free registration: there's no Razorpay payment behind it.
  paymentId: string;
  firstName: string;
  paidAt: string | null;
};

// The candidate dashboard (talent.babblebots.ai): sign in with the
// registered email, add the CV, start the interview, watch companies act.
const DASHBOARD_URL = "https://talent.babblebots.ai";
const DashboardLink = () => (
  <a className="readout-link" href={DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
    talent.babblebots.ai
  </a>
);

// What happens next, per package; the first step is always done.
const nextStepsFor = (packageId: PackageId): { title: string; body: React.ReactNode }[] => [
  {
    title: "Confirmation email sent",
    body: "Check your inbox, and your spam folder just in case.",
  },
  {
    title: "Sign in to your dashboard",
    body: (
      <>
        Go to <DashboardLink /> and sign in with the email you registered with.
      </>
    ),
  },
  packageId === "discover"
    ? {
        title: "Add your CV",
        body: "Hiring companies review it directly and reach out to you. You'll see each company's interest on your dashboard.",
      }
    : packageId === "maximize"
    ? {
        title: "Add a CV for each track and start its AI-Interview",
        body: "Each track has its own interview. Companies see your interview and CV, and you track their interest on your dashboard.",
      }
    : {
        title: "Add your CV and start the AI-Interview",
        body: "Your interview link is ready once your CV is in. Companies see your interview and CV, and you track their interest on your dashboard.",
      },
  {
    title: "WhatsApp community",
    body: "You'll be added for quick updates.",
  },
];

// Shown in place of the form once the payment is confirmed.
function RegistrationPass({
  confirmation,
  onRegisterAnother,
}: {
  confirmation: Confirmation;
  // null once registration has closed: there is nothing more to register for.
  onRegisterAnother: (() => void) | null;
}) {
  const [copied, setCopied] = useState(false);

  const paidOn = confirmation.paidAt
    ? new Date(confirmation.paidAt).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  const copyPaymentId = async () => {
    try {
      await navigator.clipboard.writeText(confirmation.paymentId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked: the id is selectable text anyway */
    }
  };

  return (
    <div className="pass-wrap" role="status" aria-live="polite">
      <div className="pass-head">
        <span className="pass-check" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
            <path
              d="M6 13.5L11 18.5L20.5 8"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="section-tag">Registration confirmed</span>
        <h3>You&rsquo;re in, {confirmation.firstName}.</h3>
        <p>
          {confirmation.trackNames.length > 1 ? (
            <>
              Your slots on the{" "}
              {confirmation.trackNames.map((name, idx) => (
                <React.Fragment key={name}>
                  {idx > 0 && (idx === confirmation.trackNames.length - 1 ? " and " : ", ")}
                  <strong>{name}</strong>
                </React.Fragment>
              ))}{" "}
              tracks are locked for this cycle.
            </>
          ) : (
            <>
              Your slot on the <strong>{confirmation.trackName}</strong> track is
              locked for this cycle.
            </>
          )}
        </p>
      </div>

      <div className="pass">
        <div className="pass-top">
          <div>
            <span className="pass-label">AI Talent Drive &middot; Entry pass</span>
            <div className="pass-track">{confirmation.trackNames.join(" \u00B7 ")}</div>
          </div>
          <span className="pass-paid">
            <span className="pass-paid-dot" aria-hidden="true" />
            Paid
          </span>
        </div>
        <div className="pass-perf" aria-hidden="true" />
        <dl className="pass-rows">
          <div>
            <dt>Package</dt>
            <dd>{confirmation.packageLabel}</dd>
          </div>
          <div>
            <dt>Amount</dt>
            <dd>{formatRupees(confirmation.amount)}</dd>
          </div>
          {paidOn && (
            <div>
              <dt>Paid on</dt>
              <dd>{paidOn}</dd>
            </div>
          )}
          {confirmation.paymentId && (
            <div>
              <dt>Payment ID</dt>
              <dd className="pass-id">
                <span>{confirmation.paymentId}</span>
                <button type="button" className="pass-copy" onClick={copyPaymentId}>
                  {copied ? "Copied" : "Copy"}
                </button>
              </dd>
            </div>
          )}
        </dl>
      </div>

      <div className="pass-next">
        <span className="pass-label">What happens next</span>
        <ol>
          {nextStepsFor(confirmation.packageId).map((step, idx) => (
            <li key={step.title} data-done={idx === 0}>
              <span className="pass-step-dot" aria-hidden="true">
                {idx === 0 ? "\u2713" : idx + 1}
              </span>
              <div>
                <strong>{step.title}</strong>
                <p>{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="pass-actions">
        {onRegisterAnother && confirmation.packageId !== "maximize" && (
          <button type="button" className="btn btn-ghost" onClick={onRegisterAnother}>
            Register for another track
          </button>
        )}
        <p className="form-fineprint">
          {confirmation.paymentId && "Keep your payment ID handy. "}Questions?{" "}
          <a className="readout-link" href="mailto:support@babblebots.ai">
            support@babblebots.ai
          </a>
        </p>
      </div>
    </div>
  );
}
type Phase = "idle" | "starting" | "paying" | "confirming";
type Toast = { kind: "success" | "error"; text: string; id: number };
const TOAST_MS = 6000;

// The countdown target: end of registration. The backend's
// HIRING_CIRCUIT_REGISTRATION_CLOSES_AT (read from /hiring-circuit/config/)
// is the source of truth once loaded; this is the fallback until then and
// should match it (extended to 15 Nov 2026, 11:59 pm IST).
const FINAL_ROUNDS_START = new Date("2026-11-15T23:59:59+05:30");

const PAIN_POINTS = [
  { icon: StackIcon, text: "Tired of applying to hundreds of companies?" },
  { icon: ChartLineDownIcon, text: "No way to track your progress?" },
  { icon: HourglassMediumIcon, text: "No interview calls?" },
  { icon: GhostIcon, text: "Companies ghosting?" },
];

const STEPS = [
  {
    title: "Register",
    body: "Pick your package and track: Basic is free and puts your CV in front of companies, Premium (₹199) adds the AI-Interview, and Max (₹799) covers three or more tracks.",
  },
  {
    title: "Complete the AI-Interview and Assessment",
    body: "Showcase your skills and personality by talking to the AI-Recruiter, Tina. She has interviewed more than 300,000 candidates.",
  },
  {
    title: "Your interview reaches 50+ companies",
    body: "Your AI-Interview is routed to 50+ companies' HR teams. Same interview, 50x the impact.",
  },
  {
    title: "Get shortlisted for final human rounds by multiple companies",
    body: "Track your progress on a dashboard.",
  },
];

const FAQS = [
  {
    q: "Can I register for more than one track?",
    a: `Yes. Max (₹799) covers three or more tracks in one payment, up to all ${TRACKS.length}, each with its own CV and AI-Interview link. You can also register separately for each track with the free Basic or ₹199 Premium package. A track you registered for free can be upgraded to Premium later with the same email.`,
  },
  {
    q: "How long does the AI interview take?",
    a: "Most candidates finish in 30–45 minutes. You can start it any time after receiving your link, from any device with a camera and microphone.",
  },
  {
    q: "Which companies will see my profile?",
    a: "Every hiring company confirmed for your selected track in this cycle. The lineup is finalized closer to the registration deadline and shared with registered candidates.",
  },
  {
    q: "What happens after I clear the AI interview?",
    a: "Hiring companies on your track review your interview and shortlist directly - they'll reach out to schedule their own human interview rounds.",
  },
  {
    q: "Is the fee refundable if I'm not shortlisted?",
    a: "The fee is non-refundable once your interview link has been issued - it covers the interview and review process itself, independent of the outcome.",
  },
];

// "track-0", "exp-2", …: one dropdown pair per picked track.
type DropdownKey = string | null;

// Every track as a numbered index row, short-term first; a row opens that
// track's job description. Shown under "Available Role Tracks".
function RoleIndex({ onOpen }: { onOpen: (track: string) => void }) {
  return (
    <div className="td-roles-groups">
      {TRACK_GROUPS.map((group) => (
        <div className="td-roles-group hc-fade" key={group.label}>
          <div className="td-roles-label">{group.label}</div>
          <ul className="td-roles-list">
            {group.tracks.map((t) => {
              const { code, name } = splitTrack(t);
              const bands = bandsFor(t);
              return (
                <li key={t}>
                  <button
                    type="button"
                    className="td-role"
                    onClick={() => onOpen(t)}
                  >
                    <span className="td-role-code">{code}</span>
                    <span className="td-role-name">{name}</span>
                    <span className="td-role-band">{bands.length > 1 ? bands.join(" / ") : bands[0]}</span>
                    <span className="td-role-cta">
                      View JD <ArrowUpRightIcon size={18} weight="bold" aria-hidden="true" />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function HiringCircuit() {
  // "" = nothing picked yet; both dropdowns start on a placeholder.
  const [packageId, setPackageId] = useState<PackageId>("interview");
  const [picks, setPicks] = useState<Pick[]>(() => emptyPicks(1));
  const [yoe, setYoe] = useState("");
  const [heardFrom, setHeardFrom] = useState("");
  // Free text shown only when "Others" is picked; required then.
  const [heardFromOther, setHeardFromOther] = useState("");
  const pkg = packageById(packageId);
  // The first chosen track, which is what the single-track copy and the JD modal use.
  const chosenTracks = picks.map((pick) => pick.track).filter(Boolean);
  const track = chosenTracks[0] || "";
  const experience = experienceFor(track, yoe);
  const setPick = (index: number, change: Partial<Pick>) =>
    setPicks((prev) => prev.map((pick, i) => (i === index ? { ...pick, ...change } : pick)));
  const choosePackage = (next: PackageId) => {
    const { minTracks, maxTracks } = packageById(next);
    setPackageId(next);
    // Keep what was picked where it still fits; add or drop rows to match.
    setPicks((prev) => [...prev.slice(0, maxTracks), ...emptyPicks(Math.max(0, minTracks - prev.length))]);
    setFieldErrors((f) => ({ ...f, track: "", experience: "", package: "" }));
  };
  // Max: a row per extra track, up to every track; rows past the minimum
  // can be taken away again.
  const addPick = () => setPicks((prev) => (prev.length < pkg.maxTracks ? [...prev, { track: "" }] : prev));
  const removePick = (index: number) =>
    setPicks((prev) => (prev.length > pkg.minTracks ? prev.filter((_, i) => i !== index) : prev));
  const yoeOptions = sharedBands(chosenTracks);
  // What the shared dropdown shows: the candidate's own pick, unless every
  // chosen track has a fixed band (then that band, and there is nothing to pick).
  const yoeShown = yoeOptions.length > 0 || chosenTracks.length === 0 ? yoe : experienceFor(chosenTracks[0], yoe);
  // Enough tracks chosen, each at a band: an empty extra row is not held against the candidate.
  const allPicked =
    chosenTracks.length >= pkg.minTracks && chosenTracks.every((t) => experienceFor(t, yoe));
  const heardLabel = HEARD_FROM_OPTIONS.find((o) => o.code === heardFrom)?.label ?? "";
  const [openDropdown, setOpenDropdown] = useState<DropdownKey>(null);
  const [openFaqs, setOpenFaqs] = useState<Set<number>>(new Set());
  const [phase, setPhase] = useState<Phase>("idle");
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<Toast | null>(null);
  const [jdTrack, setJdTrack] = useState<string | null>(null);
  // Registration closes by itself at a deadline the backend owns (it also
  // refuses late orders). Until the answer arrives the form shows as open.
  const [registrationClosed, setRegistrationClosed] = useState(false);
  // Packages whose seats are full (config.full): shown, not for sale.
  const [fullPackages, setFullPackages] = useState<ReadonlySet<PackageId>>(() => new Set());
  const [closesAt, setClosesAt] = useState<Date | null>(null);
  // The config can arrive after a package was picked: move off one that is full.
  useEffect(() => {
    if (!fullPackages.has(packageId)) return;
    const open = PACKAGES.find((p) => p.id === "interview" && !fullPackages.has(p.id)) ?? PACKAGES.find((p) => !fullPackages.has(p.id));
    if (open) choosePackage(open.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullPackages]);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const rootRef = useRef<HTMLElement>(null);

  useRevealOnScroll(rootRef);

  // Countdown to the end of registration: the backend's deadline once the
  // config has loaded, the built-in date until then.
  const countdownTarget = closesAt ?? FINAL_ROUNDS_START;
  const closesDay = countdownTarget.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    timeZone: "Asia/Kolkata",
  });
  const closesTime = countdownTarget.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
  // The phone register bar shows once the hero has scrolled away and hides
  // again while the registration section itself is on screen.
  const [barVisible, setBarVisible] = useState(false);
  useEffect(() => {
    const hero = document.getElementById("td-hero");
    const register = document.getElementById("register");
    if (!hero || !register || !("IntersectionObserver" in window)) return;
    const seen = { hero: true, register: false };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.target === hero) seen.hero = e.isIntersecting;
        else seen.register = e.isIntersecting;
      });
      setBarVisible(!seen.hero && !seen.register);
    });
    io.observe(hero);
    io.observe(register);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, countdownTarget.getTime() - Date.now());
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [countdownTarget]);

  // Close any open dropdown on outside click
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!rootRef.current) return;
      const target = e.target as Node;
      const openEl = rootRef.current.querySelector('[data-open="true"]');
      if (openEl && !openEl.contains(target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // Carousel auto-advance is driven by the timer ring in the photo's corner:
  // the ring draws one full turn (CSS, see .carousel-ring-progress) and the
  // photo changes when it completes. Hovering pauses the ring, and so the
  // advance; reduced-motion turns the ring off, and so the auto-advance.

  const toggleFaq = (idx: number) => {
    setOpenFaqs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const showToast = useCallback(
    (kind: Toast["kind"], text: string) =>
      setToast({ kind, text, id: Date.now() }),
    []
  );
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/hiring-circuit/config/`)
      .then((res) => (res.ok ? res.json() : null))
      .then((config) => {
        if (cancelled || !config) return;
        setRegistrationClosed(config.open === false);
        setClosesAt(config.closes_at ? new Date(config.closes_at) : null);
        const full: string[] = Array.isArray(config.full) ? config.full : [];
        setFullPackages(new Set(PACKAGES.map((p) => p.id).filter((id) => full.includes(id))));
      })
      .catch(() => {
        /* unreachable: leave the form open, the backend still enforces it */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A page left open across the deadline closes itself too.
  useEffect(() => {
    if (!closesAt || registrationClosed) return;
    const msLeft = closesAt.getTime() - Date.now();
    if (msLeft <= 0) {
      setRegistrationClosed(true);
      return;
    }
    // setTimeout overflows past ~24 days; only arm it once the deadline is near.
    if (msLeft > 2 ** 31 - 1) return;
    const timer = window.setTimeout(() => setRegistrationClosed(true), msLeft);
    return () => window.clearTimeout(timer);
  }, [closesAt, registrationClosed]);

  // Asks the backend whether an order is paid and, if so, shows the
  // confirmation. The backend is the only source of truth: it verifies with
  // Razorpay itself, so this is safe to call at any time, any number of times.
  // `retries` covers the few seconds a UPI payment can take to settle.
  const checkOrder = useCallback(
    async (orderId: string, retries = 0): Promise<boolean> => {
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          const res = await fetch(
            `${API_BASE}/hiring-circuit/status/?order_id=${encodeURIComponent(orderId)}`
          );
          if (res.status === 404) {
            writePendingOrder(null);
            return false;
          }
          const data = await res.json();
          if (data?.status === "paid") {
            // Only announce a payment this browser was in the middle of, not
            // a registration re-opened later from the confirmation email.
            if (readPendingOrder() === orderId)
              showToast("success", "Payment successful. You're registered!");
            writePendingOrder(null);
            const names: string[] = Array.isArray(data.tracks) && data.tracks.length
              ? data.tracks.map((t: { track_name: string }) => t.track_name)
              : [data.track_name];
            const bought = packageById(data.package || "interview");
            setConfirmation({
              trackName: data.track_name,
              trackNames: names,
              packageId: bought.id,
              packageLabel: data.package_label || bought.name,
              amount: typeof data.amount_paise === "number" ? data.amount_paise / 100 : bought.price,
              paymentId: data.payment_id,
              firstName: data.first_name,
              paidAt: data.paid_at || null,
            });
            return true;
          }
        } catch {
          /* network blip: fall through to the next attempt */
        }
        if (attempt < retries)
          await new Promise((r) => window.setTimeout(r, 3000));
      }
      return false;
    },
    [showToast]
  );

  // Arriving on /ai-talent-drive#register (e.g. the header ticker's button):
  // the router resets scroll to the top on navigation, so jump to the form
  // ourselves once it has rendered.
  useEffect(() => {
    if (!window.location.hash.startsWith("#register")) return;
    const id = window.setTimeout(() => {
      document
        .getElementById("register")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => window.clearTimeout(id);
  }, []);

  // Coming back to the page: from Razorpay's redirect, a reload, or the
  // confirmation email link. The order id rides in the URL fragment
  // (#register?order=…), never the query string: the site's CDN answers 410
  // to query strings it doesn't recognise, and fragments never reach it.
  useEffect(() => {
    const hash = window.location.hash;
    const fromUrl =
      hash.startsWith("#register?")
        ? new URLSearchParams(hash.slice("#register?".length)).get("order")
        : null;
    const orderId = fromUrl || readPendingOrder();
    if (!orderId) return;

    if (fromUrl) {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}#register`
      );
    }

    let cancelled = false;
    (async () => {
      if (fromUrl) {
        setPhase("confirming");
        document
          .getElementById("register")
          ?.scrollIntoView({ block: "start" });
      }
      const paid = await checkOrder(orderId, fromUrl ? 5 : 0);
      if (cancelled) return;
      setPhase("idle");
      if (!paid && fromUrl) showToast("error", "Payment not received yet.");
      if (!paid && fromUrl)
        setFormError(
          "We haven't received a payment for this registration yet. If money left your account, you'll get a confirmation email within a few minutes and don't need to pay again. Otherwise, please try again."
        );
    })();
    return () => {
      cancelled = true;
    };
  }, [checkOrder, showToast]);

  // Back from a UPI app (or any other tab): re-check the unfinished order.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible" || confirmation) return;
      const orderId = readPendingOrder();
      if (orderId) checkOrder(orderId);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [checkOrder, confirmation]);

  // The form unmounts while a payment is confirming and after success, and
  // comes back empty, so the dropdown picks must not carry over.
  const formHidden = !!confirmation || phase === "confirming";
  useEffect(() => {
    if (!formHidden) return;
    setPackageId("interview");
    setPicks(emptyPicks(1));
    setYoe("");
  }, [formHidden]);

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (phase !== "idle") return;

    const form = new FormData(e.currentTarget);
    const firstName = String(form.get("firstName") || "").trim();
    const lastName = String(form.get("lastName") || "").trim();
    const email = String(form.get("email") || "").trim();
    const phone = String(form.get("phone") || "").trim();

    const errors: Record<string, string> = {};
    if (fullPackages.has(packageId)) errors.package = `Seats for the ${pkg.name} package are full. Pick another package.`;
    if (!firstName) errors.first_name = "Enter your first name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errors.email = "Enter a valid email address.";
    if (!/^(\+?91|0)?[6-9]\d{9}$/.test(phone.replace(/[\s-]/g, "")))
      errors.phone = "Enter a valid 10-digit Indian mobile number.";
    if (chosenTracks.length < pkg.minTracks)
      errors.track = pkg.maxTracks === 1 ? "Select your track." : `Select at least ${pkg.minTracks} tracks.`;
    else if (new Set(chosenTracks).size !== chosenTracks.length)
      errors.track = "Pick different tracks.";
    if (chosenTracks.some((t) => !experienceFor(t, yoe)))
      errors.experience = "Select your years of experience.";
    if (!heardFrom) errors.heard_from = "Tell us where you heard about the drive.";
    else if (heardFrom === "others" && !heardFromOther.trim())
      errors.heard_from_other = "Tell us where you heard about the drive.";
    if (form.get("consent") !== "on")
      errors.consent = "Please tick the box to continue.";
    setFieldErrors(errors);
    setFormError(null);
    if (Object.keys(errors).length) {
      // Bring the first problem into view; on a phone it may be off-screen.
      const firstKey = Object.keys(errors)[0];
      const target = document.getElementById(
        { first_name: "rc-fname", email: "rc-email", phone: "rc-phone", package: "rc-package-label",
          track: "rc-track-label-0", experience: "rc-exp-label-0", heard_from: "rc-heard-label",
          heard_from_other: "rc-heard-other", consent: "rc-consent" }[firstKey] || ""
      );
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setPhase("starting");
    // "discover" is free: no Razorpay checkout follows, so there's nothing
    // to preload. The backend is still the one that decides this (`data.free`
    // below) — this only skips an unneeded script load.
    const expectsFree = pkg.price === 0;
    try {
      const [res] = await Promise.all([
        fetch(`${API_BASE}/hiring-circuit/register/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            package: packageId,
            tracks: chosenTracks.map((t) => ({
              track: trackCode(t),
              experience: experienceCode(experienceFor(t, yoe)),
            })),
            // The single-track fields the API accepted before packages.
            track: trackCode(track),
            experience: experienceCode(experience),
            first_name: firstName,
            last_name: lastName,
            email,
            phone,
            heard_from: heardFrom,
            heard_from_other: heardFrom === "others" ? heardFromOther.trim() : "",
            consent: true,
          }),
        }),
        ...(expectsFree ? [] : [loadCheckoutScript()]),
      ]);
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (data?.error === "already_registered") {
          // Often a slow UPI payment from this same browser: show its
          // confirmation instead of an error.
          const pending = readPendingOrder();
          if (pending && (await checkOrder(pending))) {
            setPhase("idle");
            return;
          }
        }
        if (data?.error === "closed") setRegistrationClosed(true);
        if (data?.fields) setFieldErrors(data.fields);
        else
          showToast(
            "error",
            data?.error === "already_registered"
              ? "You're already registered for this track."
              : data?.error === "closed"
              ? "Registrations are closed."
              : "Couldn't start your registration. Please try again."
          );
        setFormError(
          data?.message ||
            (res.status === 429
              ? "Too many attempts. Please wait a minute and try again."
              : data?.fields
              ? null
              : "Something went wrong starting your registration. Please try again.")
        );
        setPhase("idle");
        return;
      }

      if (data.free) {
        // No payment to make: the backend has already marked this paid.
        const names: string[] = Array.isArray(data.tracks) && data.tracks.length
          ? data.tracks.map((t: { track_name: string }) => t.track_name)
          : [data.track_name];
        const bought = packageById(data.package || "discover");
        setConfirmation({
          trackName: data.track_name,
          trackNames: names,
          packageId: bought.id,
          packageLabel: data.package_label || bought.name,
          amount: typeof data.amount_paise === "number" ? data.amount_paise / 100 : bought.price,
          paymentId: data.payment_id || "",
          firstName: data.first_name,
          paidAt: data.paid_at || null,
        });
        setPhase("idle");
        showToast("success", "You're registered!");
        return;
      }

      writePendingOrder(data.order_id);
      setPhase("paying");

      let settled = false;
      const confirmInPage = async (payment: Record<string, string>) => {
        settled = true;
        setPhase("confirming");
        try {
          await fetch(`${API_BASE}/hiring-circuit/return/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...payment, mode: "json" }),
          });
        } catch {
          /* checkOrder below still settles it */
        }
        const paid = await checkOrder(data.order_id, 5);
        setPhase("idle");
        if (!paid) showToast("error", "Payment is still being confirmed.");
        if (!paid)
          setFormError(
            "Your payment is still being confirmed. You'll get a confirmation email within a few minutes, and you don't need to pay again."
          );
      };

      const checkout = new (window as any).Razorpay({
        key: data.razorpay_key,
        order_id: data.order_id,
        amount: data.amount_paise,
        currency: "INR",
        name: "Babblebots Candidate Registration",
        description: `${pkg.name}: ${chosenTracks.join(", ")}`,
        prefill: data.prefill,
        // On success Razorpay sends the whole browser here (the backend then
        // forwards to this page with ?order=…). A redirect survives UPI app
        // switches and in-app browsers where the popup handler can't run.
        callback_url: data.callback_url,
        handler: confirmInPage,
        modal: {
          ondismiss: async () => {
            // Closed without an in-page success, but a UPI payment may still
            // have gone through in the other app.
            if (settled) return;
            await checkOrder(data.order_id);
            setPhase("idle");
          },
        },
        theme: { color: "#3B82F6" },
      });
      // A declined attempt. Razorpay keeps its window open for a retry, so
      // this only reports it; the form stays as it is.
      checkout.on("payment.failed", (failure: any) => {
        const reason = failure?.error?.description;
        showToast(
          "error",
          reason ? `Payment failed: ${reason}` : "Payment failed. Please try again."
        );
      });
      checkout.open();
    } catch {
      showToast("error", "Couldn't start the payment. Please try again.");
      setFormError(
        "We couldn't reach our payment provider. Check your connection and try again."
      );
      setPhase("idle");
    }
  };

  return (
    <main className="hc-root td" ref={rootRef}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wdth,wght@12..96,75..100,200..800&family=DM+Mono:wght@400;500&family=Instrument+Serif:ital@0;1&display=swap"
          rel="stylesheet"
        />
        <noscript>{`<style>.hc-root .hc-fade,.hc-root .hc-stagger>*{opacity:1!important}</style>`}</noscript>
      </Helmet>
      {jdTrack && (
        <JobDescriptionModal
          track={jdTrack}
          themeClass="td"
          initialBand={experience}
          onClose={() => setJdTrack(null)}
          onRegister={
            registrationClosed || confirmation
              ? null
              : (band) => {
                  // "Register for this track" starts a fresh single-track
                  // registration on the recommended package, for that track.
                  setPackageId("interview");
                  setPicks([{ track: jdTrack }]);
                  if (bandsFor(jdTrack).length > 1) setYoe(band);
                  setFieldErrors((f) => ({ ...f, track: "", experience: "" }));
                  setJdTrack(null);
                  document
                    .getElementById("register")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }
          }
        />
      )}
      {toast && (
        <div
          key={toast.id}
          className="hc-toast"
          data-kind={toast.kind}
          role={toast.kind === "error" ? "alert" : "status"}
        >
          <span className="hc-toast-icon" aria-hidden="true">
            {toast.kind === "success" ? "\u2713" : "!"}
          </span>
          <span className="hc-toast-text">{toast.text}</span>
          <button
            type="button"
            className="hc-toast-close"
            aria-label="Dismiss"
            onClick={() => setToast(null)}
          >
            &times;
          </button>
        </div>
      )}
      <div className="td-grain" aria-hidden="true" />

      <DriveHero closesDay={closesDay} closesTime={closesTime} closed={registrationClosed} apiBase={API_BASE} />

      <VelocityMarquee items={TRACKS.map((t) => splitTrack(t).name)} />

      {/* ---------- Track record ---------- */}
      <section className="td-band td-proof">
        <div className="wrap">
          <DriveProof />
        </div>
      </section>

      {/* ---------- Pain ---------- */}
      <section className="td-band td-pain">
        <div className="wrap td-pain__grid">
          <div className="td-head hc-fade">
            <span className="section-tag">The problem</span>
            <h2>Are you frustrated with your job search?</h2>
            <p>You are not alone. The average time to offer is ~90 days.</p>
          </div>
          <PainLines items={PAIN_POINTS} />
        </div>
      </section>

      {/* ---------- Solve ---------- */}
      <section className="td-band td-band--indigo td-solve">
        <div className="wrap td-solve__grid">
          <div className="td-solve__copy hc-fade">
            <p className="td-solve__one">
              <LightningIcon className="td-solve__bolt" weight="fill" aria-hidden="true" />
              One Interview
            </p>
            <h2>
              Automatically apply to <em>50+</em> companies
            </h2>
          </div>
          <div className="td-solve__timer hc-fade">
            <span className="td-solve__label">
              <span className="pulse" aria-hidden="true" />
              {Date.now() >= countdownTarget.getTime()
                ? "AI Job Fair is live"
                : "AI Job Fair starts in"}
            </span>
            <div
              className="td-clock"
              role="timer"
              aria-label={`${timeLeft.days} days until the AI Job Fair`}
            >
              {[
                { v: timeLeft.days, l: "Days" },
                { v: timeLeft.hours, l: "Hours" },
                { v: timeLeft.minutes, l: "Mins" },
                { v: timeLeft.seconds, l: "Secs" },
              ].map((t) => (
                <div className="td-clock-cell" key={t.l}>
                  {/* re-keyed on its value so each change ticks in */}
                  <span className="td-clock-val" key={t.v}>
                    {String(t.v).padStart(2, "0")}
                  </span>
                  <span className="td-clock-label">{t.l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Companies hiring (hidden until at least one is listed) ---------- */}
      <div className="td-band td-band--paper td-logos">
        <div className="wrap">
          <DriveCompanyLogos />
        </div>
      </div>

      {/* ---------- Available role tracks ---------- */}
      <section id="roles" className="td-band td-roles">
        <div className="wrap">
          <div className="td-head hc-fade">
            <span className="section-tag">{TRACKS.length} role tracks</span>
            <h2>Available Role Tracks</h2>
          </div>
          <RoleIndex onOpen={setJdTrack} />
        </div>
      </section>

      {/* ---------- How it works ---------- */}
      <section id="how" className="td-band td-how">
        <div className="wrap">
          <div className="td-head hc-fade">
            <span className="section-tag">Four steps</span>
            <h2>How does it work?</h2>
          </div>
          <ol className="td-steps hc-stagger">
            {STEPS.map((step, idx) => (
              <li className="td-step" key={step.title}>
                <span className="td-step-num" aria-hidden="true">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <h3>
                  <span className="td-sr">Step {idx + 1}: </span>
                  {step.title}
                </h3>
                <p>{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ---------- Candidate ratings and drive reels ---------- */}
      {/* Each renders nothing until it has something to show (see the
          components), so the band carries no padding of its own. */}
      <div className="td-voices">
        <div className="wrap">
          <CandidateRatings apiBase={API_BASE} />
          <DriveReels apiBase={API_BASE} />
        </div>
      </div>

      {/* ---------- Registration ---------- */}
      <section id="register" className="td-band td-band--indigo td-register">
        <div className="wrap">
          <div className="td-head hc-fade">
            <span className="section-tag">Your ticket</span>
            <h2>Registration Information</h2>
            {!registrationClosed && (
              <p className="td-closes">
                Registration closes <b>{closesDay}</b>, {closesTime} IST
              </p>
            )}
          </div>

          {!confirmation && !registrationClosed && (
            <PackageCards selected={packageId} full={fullPackages} onSelect={choosePackage} />
          )}

          <div className="socket socket--form hc-fade">
            <div className="form-card">
              {confirmation ? (
                <RegistrationPass
                  confirmation={confirmation}
                  onRegisterAnother={
                    registrationClosed
                      ? null
                      : () => {
                          setConfirmation(null);
                          setFormError(null);
                          setFieldErrors({});
                        }
                  }
                />
              ) : phase === "confirming" ? (
                <div
                  className="form-success"
                  style={{ display: "block" }}
                  role="status"
                >
                  <span className="pass-spinner" aria-hidden="true" />
                  <h3>Confirming your payment&hellip;</h3>
                  <p>This takes a few seconds. Please keep this page open.</p>
                </div>
              ) : registrationClosed ? (
                <div className="form-success form-closed" style={{ display: "block" }}>
                  <span className="section-tag">Registration closed</span>
                  <h3>Registrations for this cycle are closed</h3>
                  <p>
                    Final round interviews are underway, so we&rsquo;re no
                    longer taking new registrations for this cycle.
                  </p>
                  <p>
                    Already registered? Sign in at <DashboardLink /> with the
                    email you registered with. For anything else, write to{" "}
                    <a className="readout-link" href="mailto:support@babblebots.ai">
                      support@babblebots.ai
                    </a>
                    .
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className="field">
                    <span id="rc-package-label" className="field-label">Package</span>
                    <div className="dd" data-open={openDropdown === "package"}>
                      <button
                        type="button"
                        className="dd-trigger pk-dd-trigger"
                        aria-haspopup="listbox"
                        aria-expanded={openDropdown === "package"}
                        aria-labelledby="rc-package-label dd-package-value"
                        onClick={() =>
                          setOpenDropdown((d) => (d === "package" ? null : "package"))
                        }
                      >
                        <span id="dd-package-value">
                          <strong>{pkg.name}</strong> &middot; <PriceDisplay pkg={pkg} />
                          {pkg.maxTracks > 1 && <span className="pk-selected-note"> &middot; {pkg.minTracks} to {pkg.maxTracks} tracks</span>}
                        </span>
                        <svg
                          className="select-arrow"
                          width="11"
                          height="7"
                          viewBox="0 0 11 7"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M1 1L5.5 5.5L10 1"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <ul
                        className="dd-menu"
                        role="listbox"
                        aria-labelledby="rc-package-label"
                        tabIndex={-1}
                        hidden={openDropdown !== "package"}
                      >
                        {PACKAGES.map((option) => {
                          const full = fullPackages.has(option.id);
                          return (
                            <li
                              key={option.id}
                              role="option"
                              aria-selected={option.id === packageId}
                              aria-disabled={full}
                              className={full ? "dd-taken" : undefined}
                              onClick={() => {
                                if (full) return;
                                choosePackage(option.id);
                                setOpenDropdown(null);
                              }}
                            >
                              <span>
                                <strong>{option.name}</strong> &middot; <PriceDisplay pkg={option} />
                                <span className="pk-dd-tagline">{full ? "Seats are full" : option.tagline}</span>
                              </span>
                              <span className="dd-check" aria-hidden="true">
                                {full ? "FULL" : "✓"}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    {fieldErrors.package && (
                      <p className="field-error">{fieldErrors.package}</p>
                    )}
                  </div>

                  <div className="field-row">
                    <div className="field">
                      <label htmlFor="rc-fname">First name</label>
                      <input
                        id="rc-fname"
                        name="firstName"
                        type="text"
                        placeholder="Ananya"
                        required
                        aria-invalid={!!fieldErrors.first_name}
                      />
                      {fieldErrors.first_name && (
                        <p className="field-error">{fieldErrors.first_name}</p>
                      )}
                    </div>
                    <div className="field">
                      <label htmlFor="rc-lname">Last name</label>
                      <input
                        id="rc-lname"
                        name="lastName"
                        type="text"
                        placeholder="Rao"
                      />
                    </div>
                  </div>

                  <div className="field-stack">
                    <div className="field">
                      <label htmlFor="rc-email">Email</label>
                      <input
                        id="rc-email"
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        required
                        aria-invalid={!!fieldErrors.email}
                      />
                      {fieldErrors.email && (
                        <p className="field-error">{fieldErrors.email}</p>
                      )}
                    </div>
                    <div className="field">
                      <label htmlFor="rc-phone">Phone</label>
                      <input
                        id="rc-phone"
                        name="phone"
                        type="tel"
                        placeholder="+91 90000 00000"
                        required
                        aria-invalid={!!fieldErrors.phone}
                      />
                      {fieldErrors.phone && (
                        <p className="field-error">{fieldErrors.phone}</p>
                      )}
                    </div>
                  </div>

                  <div className={pkg.maxTracks > 1 ? "pick-grid" : "pick-row"}>
                    <div className="field">
                      <span id="rc-exp-label-0" className="field-label">Years of experience</span>
                      <div className="dd" data-open={openDropdown === "exp"}>
                        <button
                          type="button"
                          className="dd-trigger"
                          aria-haspopup="listbox"
                          aria-expanded={openDropdown === "exp"}
                          aria-labelledby="rc-exp-label-0 dd-exp-value-0"
                          onClick={() => setOpenDropdown((d) => (d === "exp" ? null : "exp"))}
                        >
                          <span
                            id="dd-exp-value-0"
                            data-placeholder={!yoeShown}
                            title={yoeShown || undefined}
                          >
                            {yoeShown || "Select your experience"}
                          </span>
                          <svg
                            className="select-arrow"
                            width="11"
                            height="7"
                            viewBox="0 0 11 7"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M1 1L5.5 5.5L10 1"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <ul
                          className="dd-menu"
                          role="listbox"
                          aria-labelledby="rc-exp-label-0"
                          tabIndex={-1}
                          hidden={openDropdown !== "exp"}
                        >
                          {yoeOptions.length === 0 && (
                            <li className="dd-empty" role="option" aria-disabled="true" aria-selected={false}>
                              {yoeShown}
                            </li>
                          )}
                          {yoeOptions.map((opt) => (
                            <li
                              key={opt}
                              role="option"
                              aria-selected={opt === yoe}
                              onClick={() => {
                                setYoe(opt);
                                setFieldErrors((f) => ({ ...f, experience: "" }));
                                setOpenDropdown(null);
                              }}
                            >
                              {opt}
                              <span className="dd-check" aria-hidden="true">
                                &#10003;
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {pkg.maxTracks > 1 && chosenTracks.some((t) => bandsFor(t).length === 1) && (
                        <p className="field-note">
                          {chosenTracks
                            .filter((t) => bandsFor(t).length === 1)
                            .map((t) => `${splitTrack(t).code}: ${bandsFor(t)[0]}`)
                            .join(" \u00B7 ")}
                        </p>
                      )}
                      {fieldErrors.experience && (
                        <p className="field-error">{fieldErrors.experience}</p>
                      )}
                    </div>

                    {picks.map((pick, index) => {
                      const trackKey = `track-${index}`;
                      const takenElsewhere = picks
                        .filter((_, i) => i !== index)
                        .map((other) => other.track)
                        .filter(Boolean);
                      return (
                        <div className="field" key={index}>
                          <span className="pick-head">
                            <span id={`rc-track-label-${index}`} className="field-label">
                              {pkg.maxTracks === 1 ? "Select track" : `Track ${index + 1}`}
                            </span>
                            {picks.length > pkg.minTracks && index >= pkg.minTracks && (
                              <button
                                type="button"
                                className="pick-remove"
                                onClick={() => removePick(index)}
                                aria-label={`Remove track ${index + 1}`}
                              >
                                Remove
                              </button>
                            )}
                          </span>
                          <div className="dd" data-open={openDropdown === trackKey}>
                            <button
                              type="button"
                              className="dd-trigger"
                              aria-haspopup="listbox"
                              aria-expanded={openDropdown === trackKey}
                              aria-labelledby={`rc-track-label-${index} dd-track-value-${index}`}
                              onClick={() =>
                                setOpenDropdown((d) => (d === trackKey ? null : trackKey))
                              }
                            >
                              <span
                                id={`dd-track-value-${index}`}
                                data-placeholder={!pick.track}
                                title={pick.track || undefined}
                              >
                                {pick.track || "Select your track"}
                              </span>
                              <svg
                                className="select-arrow"
                                width="11"
                                height="7"
                                viewBox="0 0 11 7"
                                fill="none"
                                aria-hidden="true"
                              >
                                <path
                                  d="M1 1L5.5 5.5L10 1"
                                  stroke="currentColor"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <ul
                              className="dd-menu"
                              role="listbox"
                              aria-labelledby={`rc-track-label-${index}`}
                              tabIndex={-1}
                              hidden={openDropdown !== trackKey}
                            >
                              {TRACKS.map((t) => {
                                const taken = takenElsewhere.includes(t);
                                return (
                                  <li
                                    key={t}
                                    role="option"
                                    aria-selected={t === pick.track}
                                    aria-disabled={taken}
                                    className={taken ? "dd-taken" : undefined}
                                    onClick={() => {
                                      if (taken) return;
                                      setPick(index, { track: t });
                                      setFieldErrors((f) => ({ ...f, track: "" }));
                                      setOpenDropdown(null);
                                    }}
                                  >
                                    {t}
                                    <span className="dd-check" aria-hidden="true">
                                      {taken ? "picked above" : "\u2713"}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                          {index === picks.length - 1 && fieldErrors.track && (
                            <p className="field-error">{fieldErrors.track}</p>
                          )}
                        </div>
                      );
                    })}
                    {pkg.maxTracks > picks.length && (
                      <div className="field">
                        <button type="button" className="pick-add" onClick={addPick}>
                          + Add another track
                          <span className="pick-add-note">
                            {" "}
                            &middot; up to {pkg.maxTracks}, same &#8377;{pkg.price.toLocaleString("en-IN")}
                          </span>
                        </button>
                      </div>
                    )}

                  </div>

                  <div className="pick-row">
                    <div className="field">
                      <span id="rc-heard-label" className="field-label">Where did you hear about us?</span>
                      <div className="dd dd-up" data-open={openDropdown === "heard"}>
                        <button
                          type="button"
                          className="dd-trigger"
                          aria-haspopup="listbox"
                          aria-expanded={openDropdown === "heard"}
                          aria-labelledby="rc-heard-label dd-heard-value"
                          onClick={() => setOpenDropdown((d) => (d === "heard" ? null : "heard"))}
                        >
                          <span id="dd-heard-value" data-placeholder={!heardFrom}>
                            {heardLabel || "Select one"}
                          </span>
                          <svg
                            className="select-arrow"
                            width="11"
                            height="7"
                            viewBox="0 0 11 7"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M1 1L5.5 5.5L10 1"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <ul
                          className="dd-menu"
                          role="listbox"
                          aria-labelledby="rc-heard-label"
                          tabIndex={-1}
                          hidden={openDropdown !== "heard"}
                        >
                          {HEARD_FROM_OPTIONS.map((opt) => (
                            <li
                              key={opt.code}
                              role="option"
                              aria-selected={opt.code === heardFrom}
                              onClick={() => {
                                setHeardFrom(opt.code);
                                setFieldErrors((f) => ({ ...f, heard_from: "", heard_from_other: "" }));
                                setOpenDropdown(null);
                              }}
                            >
                              {opt.label}
                              <span className="dd-check" aria-hidden="true">
                                &#10003;
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {fieldErrors.heard_from && (
                        <p className="field-error">{fieldErrors.heard_from}</p>
                      )}
                      {heardFrom === "others" && (
                        <>
                          <input
                            id="rc-heard-other"
                            type="text"
                            value={heardFromOther}
                            onChange={(e) => {
                              setHeardFromOther(e.target.value);
                              setFieldErrors((f) => ({ ...f, heard_from_other: "" }));
                            }}
                            placeholder="Please tell us where (a friend, college group, newsletter…)"
                            maxLength={200}
                            required
                            autoFocus
                            aria-label="Where did you hear about us? (Others)"
                            aria-invalid={!!fieldErrors.heard_from_other}
                            style={{ marginTop: 8 }}
                          />
                          {fieldErrors.heard_from_other && (
                            <p className="field-error">{fieldErrors.heard_from_other}</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <label className="consent" htmlFor="rc-consent">
                    <input
                      id="rc-consent"
                      name="consent"
                      type="checkbox"
                      aria-invalid={!!fieldErrors.consent}
                      onChange={() =>
                        setFieldErrors((f) => ({ ...f, consent: "" }))
                      }
                    />
                    <span>
                      I agree to receive updates from Babblebots via WhatsApp
                      and email.
                    </span>
                  </label>
                  {fieldErrors.consent && (
                    <p className="field-error consent-error">{fieldErrors.consent}</p>
                  )}

                  {formError && (
                    <div className="form-alert" role="alert">
                      <span className="form-alert-icon" aria-hidden="true">
                        !
                      </span>
                      <p>{formError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-copper form-submit"
                    // Max needs at least three tracks (and their bands) before paying:
                    // the button stays live and the submit check says what is missing.
                    disabled={phase !== "idle"}
                  >
                    {phase === "idle" ? (
                      pkg.price === 0 ? (
                        <>
                          Complete registration{" "}
                          <span className="btn-price">
                            - <PriceDisplay pkg={pkg} />
                          </span>
                        </>
                      ) : (
                        <>
                          Continue to payment{" "}
                          <span className="btn-price">- {formatRupees(pkg.price)}</span>
                        </>
                      )
                    ) : pkg.price === 0 ? (
                      "Registering\u2026"
                    ) : (
                      "Opening payment\u2026"
                    )}
                  </button>
                  <p className="form-fineprint">
                    <strong>{pkg.name}</strong>
                    {pkg.maxTracks > 1 && !allPicked
                      ? ` - pick at least ${pkg.minTracks} different tracks to continue.`
                      : track
                      ? ` - ${picks.map((pick) => pick.track).filter(Boolean).join(", ")}.`
                      : "."}{" "}
                    {pkg.price > 0 && "Fee is non-refundable once your dashboard access is issued. "}
                    {closesAt && (
                      <>
                        {" "}
                        Registration closes on{" "}
                        <strong>
                          {closesAt.toLocaleString("en-IN", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            timeZone: "Asia/Kolkata",
                          })}{" "}
                          IST
                        </strong>
                        .
                      </>
                    )}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="td-band td-faq">
        <div className="wrap td-faq__grid">
          <div className="td-head td-faq__head hc-fade">
            <span className="section-tag">Before you register</span>
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="td-faq__list hc-stagger">
            {FAQS.map((item, idx) => {
              const isOpen = openFaqs.has(idx);
              return (
                <div className="faq-item" data-open={isOpen} key={item.q}>
                  <button
                    className="faq-q"
                    aria-expanded={isOpen}
                    onClick={() => toggleFaq(idx)}
                  >
                    {item.q}
                    <span className="faq-mark">+</span>
                  </button>
                  <div
                    className="faq-a"
                    style={{ maxHeight: isOpen ? 520 : 0 }}
                  >
                    <p>{item.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Outro ---------- */}
      <section className="td-band td-band--lime td-outro">
        <div className="wrap td-outro__inner">
          <h2>
            One <em>interview.</em> 50+ <em>companies.</em>
          </h2>
          <div className="td-outro__foot">
            {!registrationClosed && (
              <button
                type="button"
                className="td-btn td-btn--ink"
                onClick={() =>
                  document
                    .getElementById("register")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                <span>Register as a candidate</span>
                <ArrowRightIcon size={20} weight="bold" aria-hidden="true" />
              </button>
            )}
            <p>
              Questions? Write to{" "}
              <a href="mailto:support@babblebots.ai">support@babblebots.ai</a>
            </p>
          </div>
        </div>
      </section>

      {barVisible && !confirmation && !registrationClosed && (
        <div className="td-bar" role="region" aria-label="Register for the drive">
          <span className="td-bar__time">
            <b>
              {timeLeft.days}d {String(timeLeft.hours).padStart(2, "0")}h
            </b>{" "}
            left
          </span>
          <button
            type="button"
            className="td-btn td-btn--lime td-btn--sm"
            onClick={() =>
              document
                .getElementById("register")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            Register as a candidate
            <ArrowRightIcon size={18} weight="bold" aria-hidden="true" />
          </button>
        </div>
      )}
    </main>
  );
}
